// ============================================================================
// Canonical Chip Registry — single source of truth for all suggestion chips
// across all 3 pipelines (frontend static, backend LLM/static, EmptyState).
//
// Every suggestion chip displayed anywhere in SpecTree MUST be defined here.
// This ensures consistent wording and prevents conflicting suggestions.
// ============================================================================

import type { SuggestedChip } from "@/lib/api/chat";

// ---------------------------------------------------------------------------
// Canonical chips per page type
// ---------------------------------------------------------------------------

/**
 * All approved suggestion chips, keyed by page type.
 *
 * These are the only chips that pipelines should produce. The
 * `matchSuggestedChipsToApprovedChips()` validator in suggestion-validator.ts
 * uses this set to filter/replace externally generated chips.
 */
export const CANONICAL_CHIPS: Record<string, SuggestedChip[]> = {
  dashboard: [
    {
      label: "Review active epics",
      value: "Take me to my epics and help me review what needs attention",
      icon: "arrow-right",
    },
    {
      label: "How do I get started?",
      value: "Walk me through the one-time SpecTree setup in simple steps.",
      icon: "rocket",
    },
    {
      label: "What agents are available?",
      value: "What SpecTree agents are available and when should I use each one?",
    },
    {
      label: "Check my tasks",
      value: "Show me my assigned tasks and what needs attention",
      icon: "user",
    },
    {
      label: "See blocked work",
      value: "Show me blocked work and where I can resolve it",
      icon: "alert",
    },
    {
      label: "What do the metrics mean?",
      value: "Explain what each metric card on the dashboard represents",
    },
    {
      label: "How do I change the time range?",
      value: "Show me how to change the interval for the dashboard charts",
    },
  ],

  "epic-list": [
    {
      label: "Create a new epic",
      value: "Walk me through creating a new epic with the SpecTree agents and explain each step simply.",
    },
    {
      label: "Review requests first",
      value: "Take me to epic requests so I can review incoming ideas first",
    },
    {
      label: "Filter this backlog",
      value: "Show me how to filter this epic list to find the right work",
    },
    {
      label: "What does the progress bar show?",
      value: "Explain what the progress bar on each epic card means",
    },
    {
      label: "Open an epic",
      value: "How do I open an epic to see its features and tasks?",
    },
  ],

  "epic-detail": [
    {
      label: "Run this epic",
      value: "Walk me through running this epic with the SpecTree orchestrator and tell me the exact command to use.",
    },
    {
      label: "Review its features",
      value: "Show me the features in this epic and what to look at first",
    },
    {
      label: "Add a feature",
      value: "Help me add a new feature to this epic",
      icon: "plus",
    },
    {
      label: "View execution plan",
      value: "Show me the execution plan for this epic",
      icon: "list",
    },
    {
      label: "Epic best practices",
      value: "What should I check before I start running this epic?",
      icon: "book",
    },
  ],

  "feature-detail": [
    {
      label: "Update feature status",
      value: "Show me how to change the status of this feature",
    },
    {
      label: "Break into tasks",
      value: "Help me break this feature into clear tasks",
      icon: "plus",
    },
    {
      label: "Check dependencies",
      value: "Help me review dependencies for this feature",
      icon: "link",
    },
  ],

  "task-detail": [
    {
      label: "Update this task",
      value: "Show me how to update this task",
    },
    {
      label: "Mark progress",
      value: "Help me log progress or change the status of this task",
      icon: "check",
    },
    {
      label: "Open parent feature",
      value: "Take me to the parent feature for this task",
    },
    {
      label: "Add validations",
      value: "Walk me through adding validation checks to this task",
      icon: "shield",
    },
  ],

  "team-list": [
    {
      label: "Open a team",
      value: "Help me choose the right team to open from this list",
    },
    {
      label: "Manage members",
      value: "Show me where team members are managed",
    },
    {
      label: "How do teams work?",
      value: "Explain how teams are organized in SpecTree",
    },
  ],

  "team-detail": [
    {
      label: "Manage members",
      value: "Show me how to manage members for this team",
    },
    {
      label: "Update settings",
      value: "Take me to the settings I can manage for this team",
    },
  ],

  settings: [
    {
      label: "Change preferences",
      value: "Show me where to update my preferences",
    },
    {
      label: "Review settings",
      value: "Walk me through the settings available on this page",
    },
  ],

  inbox: [
    {
      label: "Review unread updates",
      value: "Help me review unread notifications from this inbox",
    },
    {
      label: "Catch up quickly",
      value: "Summarize the most important recent activity from this inbox",
    },
  ],

  "whats-new": [
    {
      label: "Scan recent changes",
      value: "Highlight the most important recent product changes here",
    },
    {
      label: "Open changelog",
      value: "Take me to the latest changelog details",
    },
  ],

  "epic-request-list": [
    {
      label: "Submit a request",
      value: "Guide me through submitting a new epic request",
    },
    {
      label: "Review incoming ideas",
      value: "Help me review the epic requests on this page",
    },
    {
      label: "Explain this workflow",
      value: "Explain how epic requests move from proposal to implementation",
    },
    {
      label: "Filter by status",
      value: "Show me how to filter requests by their approval status",
    },
    {
      label: "What happens after approval?",
      value: "What happens to an epic request after it gets approved?",
    },
  ],

  "epic-request-detail": [
    {
      label: "React to request",
      value: "Show me how to react to this epic request",
    },
    {
      label: "Add a comment",
      value: "Help me add a useful comment to this epic request",
    },
  ],

  "admin-users": [
    {
      label: "Manage users",
      value: "Show me where to manage users from this admin page",
    },
    {
      label: "Review roles",
      value: "Help me review the user roles available from this page",
    },
  ],

  onboarding: [
    {
      label: "Set up my environment",
      value: "I'm new to SpecTree. Walk me through the setup in simple words and tell me what goes in my normal terminal versus the agent CLI session.",
      icon: "rocket",
    },
    {
      label: "How does this work?",
      value: "Explain the SpecTree workflow — how do epics get created and executed using the SpecTree agents?",
    },
    {
      label: "Available agents",
      value: "What agents does SpecTree have, and when should I use each one?",
    },
    {
      label: "Restart setup guide",
      value: "__restart_onboarding__",
      icon: "refresh",
    },
  ],
};

// ---------------------------------------------------------------------------
// Default / fallback chips (when no page-type match exists)
// ---------------------------------------------------------------------------

/**
 * Default chips shown when the EmptyState has no context-specific suggestions.
 * Also used as the fallback when the backend has no page-type match.
 */
export const DEFAULT_CHIPS: SuggestedChip[] = [
  {
    label: "Review my epics",
    value: "Take me to my epics and show me what to review first",
  },
  {
    label: "Start an epic request",
    value: "Take me to epic requests so I can submit a new idea",
  },
  {
    label: "Check blocked work",
    value: "Show me blocked work and where I should go next",
  },
  {
    label: "Find a feature",
    value: "Help me find a specific feature in SpecTree",
  },
  {
    label: "Learn this page",
    value: "Explain what I can do on this page and where to start",
  },
  {
    label: "Where should I start?",
    value: "What is the best place for me to start in SpecTree right now?",
    icon: "rocket",
  },
  {
    label: "Pick the right workflow",
    value: "Help me choose the right SpecTree workflow for what I need to do",
    icon: "question",
  },
];

// ---------------------------------------------------------------------------
// Onboarding-stage-aware chips (secondary conversational entry points)
// ---------------------------------------------------------------------------

/**
 * Chips shown to brand-new users (stage: 'new').
 * These complement the Help Center — they're secondary conversational entry points in Sprout.
 */
export const ONBOARDING_CHIPS_NEW: SuggestedChip[] = [
  {
    label: "Set up my environment",
    value: "I'm new to SpecTree. Walk me through the setup in simple words and tell me what goes in my normal terminal versus the Claude Code session.",
    icon: "rocket",
  },
  {
    label: "What is SpecTree?",
    value: "Explain what SpecTree does and how the workflow works — epic requests, planning, orchestration, and review.",
  },
  {
    label: "Show me around",
    value: "Give me a quick tour of the SpecTree interface — what each section does and where to start.",
    icon: "arrow-right",
  },
];

/**
 * Chips shown to users who completed setup but haven't created content (stage: 'setup-complete').
 */
export const ONBOARDING_CHIPS_SETUP_COMPLETE: SuggestedChip[] = [
  {
    label: "Create my first request",
    value: "Walk me through creating my first epic request using the request-formulator agent.",
    icon: "rocket",
  },
  {
    label: "Review existing work",
    value: "Show me the existing epics and requests so I can understand what's in progress.",
    icon: "arrow-right",
  },
  {
    label: "How do agents work?",
    value: "Explain the SpecTree agents — planner, orchestrator, reviewer — and when to use each one.",
  },
];

// ---------------------------------------------------------------------------
// Follow-up chips (shown after assistant responses)
// ---------------------------------------------------------------------------

/**
 * Page-specific follow-up chips keyed by normalized pathname.
 * Shown after an assistant response based on current route.
 */
export const PAGE_FOLLOW_UP_CHIPS: Record<string, SuggestedChip[]> = {
  "/": [
    { label: "Walk me through the dashboard", value: "Give me a tour of everything on this page" },
    { label: "What do the metrics mean?", value: "Explain what each metric card on the dashboard represents" },
    { label: "How do I change the time range?", value: "Show me how to change the interval for the dashboard charts" },
    { label: "Where are my epics?", value: "Take me to my epics so I can see my work" },
    { label: "How do I get started?", value: "What's the first thing I should do as a new user on SpecTree?" },
  ],
  "/epics": [
    { label: "How do I create an epic?", value: "Walk me through creating a new epic with the SpecTree agents." },
    { label: "Walk me through this page", value: "Give me a tour of everything on this page" },
    { label: "How do I filter epics?", value: "Show me how to filter and find specific epics" },
    { label: "What does the progress bar show?", value: "Explain what the progress bar on each epic card means" },
    { label: "Open an epic", value: "How do I open an epic to see its features and tasks?" },
    { label: "Submit a proposal instead", value: "Take me to epic requests to submit a proposal" },
  ],
  "/epic-requests": [
    { label: "Submit a new request", value: "How do I create a new epic request?" },
    { label: "Walk me through this page", value: "Give me a tour of everything on this page" },
    { label: "Filter by status", value: "Show me how to filter requests by their approval status" },
    { label: "What happens after approval?", value: "What happens to an epic request after it gets approved?" },
    { label: "Go to my epics", value: "Take me to my epics" },
  ],
  "/teams": [
    { label: "Walk me through this page", value: "Give me a tour of everything on this page" },
    { label: "How do teams work?", value: "Explain how teams are organized in SpecTree" },
    { label: "Go to my epics", value: "Take me to my epics" },
  ],
};

/**
 * Follow-up chips based on the type of content just shown to the user.
 */
export const CONTENT_FOLLOW_UP_CHIPS: Record<string, SuggestedChip[]> = {
  terminal: [
    { label: "Which command do I use?", value: "Which SpecTree command should I use for this workflow?" },
    { label: "How do I set this up?", value: "Walk me through the one-time setup for using SpecTree with your CLI agent." },
  ],
  tour: [
    { label: "What else can I do here?", value: "What other actions are available on this page?" },
  ],
  navigation: [
    { label: "What can I do here?", value: "Explain what I can do on this page and where to start" },
  ],
  text: [
    { label: "Tell me more", value: "Can you expand on that with more detail?" },
    { label: "What should I do next?", value: "Based on what you just told me, what should I do next?" },
  ],
};

/**
 * General fallback follow-up chips when no content-type or page-specific match.
 */
export const GENERAL_FOLLOW_UP_CHIPS: SuggestedChip[] = [
  { label: "Walk me through this page", value: "Give me a tour of everything on this page" },
  { label: "How do I use SpecTree?", value: "What's the first thing I should do as a new user on SpecTree?" },
  { label: "How do the SpecTree commands work?", value: "Explain which SpecTree commands I should use for the main SpecTree workflows." },
  { label: "Where are my epics?", value: "Take me to my epics so I can see my work" },
];

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Get all canonical chips for a given page type, falling back to DEFAULT_CHIPS.
 */
export function getCanonicalChips(pageType: string): SuggestedChip[] {
  return CANONICAL_CHIPS[pageType] ?? DEFAULT_CHIPS;
}

/**
 * Get a flat list of all unique chip labels across every page type.
 * Useful for building LLM prompts that enumerate approved labels.
 */
export function getAllCanonicalLabels(): string[] {
  const labels = new Set<string>();
  for (const chips of Object.values(CANONICAL_CHIPS)) {
    for (const chip of chips) {
      labels.add(chip.label);
    }
  }
  for (const chip of DEFAULT_CHIPS) {
    labels.add(chip.label);
  }
  return Array.from(labels);
}

/**
 * Get all unique chips (by label) across every page type + defaults.
 * Useful for validation and testing.
 */
export function getAllCanonicalChips(): SuggestedChip[] {
  const seen = new Set<string>();
  const result: SuggestedChip[] = [];

  for (const chips of Object.values(CANONICAL_CHIPS)) {
    for (const chip of chips) {
      const key = chip.label.toLowerCase();
      if (!seen.has(key)) {
        seen.add(key);
        result.push(chip);
      }
    }
  }
  for (const chip of DEFAULT_CHIPS) {
    const key = chip.label.toLowerCase();
    if (!seen.has(key)) {
      seen.add(key);
      result.push(chip);
    }
  }

  return result;
}
